import * as Yup from "yup";

/**
 * Pure, side-effect-free validation helpers for the redesigned Send Receipt form.
 *
 * These are intentionally framework-agnostic (plain functions) so they can be
 * exercised directly by property-based tests, and are additionally exposed as
 * Yup-compatible validators/schemas so they can be composed into a Formik +
 * Yup form (see SendReceiptForm).
 *
 * Rules are defined by the feature requirements (9.3, 9.4):
 *  - Email: non-empty, exactly one "@", at least one character before the "@",
 *    the domain portion (after the "@") must contain at least one ".", and the
 *    overall length must be <= 254 characters.
 *  - Mobile: 10 to 15 digits (inclusive) after stripping spaces and common
 *    formatting characters (spaces, dashes, parentheses, plus sign, dots).
 */

/** Maximum allowed length of an email address. */
export const MAX_EMAIL_LENGTH = 254;

/** Minimum number of digits for a valid mobile number. */
export const MIN_MOBILE_DIGITS = 10;

/** Maximum number of digits for a valid mobile number. */
export const MAX_MOBILE_DIGITS = 15;

/**
 * Determines whether the supplied value is a valid email address per the
 * receipt-email rule.
 *
 * A value is valid when, after trimming, it is non-empty, is at most 254
 * characters, contains exactly one "@", has at least one character before the
 * "@", and the domain portion after the "@" contains at least one ".".
 *
 * @param {unknown} value The candidate email address.
 * @returns {boolean} True when the value satisfies the email rule.
 */
export const isValidEmail = (value) => {
  if (typeof value !== "string") {
    return false;
  }

  const email = value.trim();

  if (email.length === 0 || email.length > MAX_EMAIL_LENGTH) {
    return false;
  }

  const atParts = email.split("@");
  if (atParts.length !== 2) {
    return false;
  }

  const [localPart, domainPart] = atParts;

  if (localPart.length < 1) {
    return false;
  }

  return domainPart.includes(".");
};

/**
 * Strips spaces and common formatting characters (spaces, dashes, parentheses,
 * plus sign, and dots) from a mobile-number string, leaving only the remaining
 * characters (typically digits).
 *
 * @param {string} value The raw mobile-number input.
 * @returns {string} The input with formatting characters removed.
 */
export const stripMobileFormatting = (value) =>
  value.replace(/[\s\-().+]/g, "");

/**
 * Determines whether the supplied value is a valid mobile number per the
 * receipt-mobile rule.
 *
 * A value is valid when, after stripping spaces and formatting characters
 * (spaces, dashes, parentheses, plus sign, dots), the remaining string consists
 * solely of digits and has a length between 10 and 15 (inclusive).
 *
 * @param {unknown} value The candidate mobile number.
 * @returns {boolean} True when the value satisfies the mobile rule.
 */
export const isValidMobile = (value) => {
  if (typeof value !== "string") {
    return false;
  }

  const digits = stripMobileFormatting(value);

  if (digits.length < MIN_MOBILE_DIGITS || digits.length > MAX_MOBILE_DIGITS) {
    return false;
  }

  return /^\d+$/.test(digits);
};

/**
 * A Yup string schema enforcing the receipt-email rule. Composable into a larger
 * Yup object schema (e.g. in SendReceiptForm).
 */
export const emailValidator = Yup.string()
  .required("* Required")
  .test(
    "valid-receipt-email",
    "Invalid email address",
    (value) => isValidEmail(value)
  );

/**
 * A Yup string schema enforcing the receipt-mobile rule. Composable into a
 * larger Yup object schema (e.g. in SendReceiptForm).
 */
export const mobileValidator = Yup.string()
  .required("* Required")
  .test(
    "valid-receipt-mobile",
    "Mobile number is not valid",
    (value) => isValidMobile(value)
  );
